import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { SPOKES, ASI_TEMPLATE_ID, RFI_RESPONSE_TEMPLATE_ID, SUBMITTAL_REVIEW_TEMPLATE_ID } from '../outline/constants.js'
import { getMilestoneTemplates, createMilestone } from '../services/milestones.js'
import { seedMilestoneChecklist } from '../services/checklist.js'
import { getProjectContracts } from '../services/bidCampaigns.js'
import { createAsi, getNextAsiNumber } from '../services/asis.js'
import { createRfi, getNextRfiNumber } from '../services/rfis.js'
import { createSubmittal } from '../services/submittals.js'
import Select from './Select.jsx'

// Human labels for the "Linking ___ record" progress step and the Configure-step helper text,
// keyed by the same caKind values computed below.
const CA_KIND_LABEL = { asi: 'ASI', rfi: 'RFI', submittal: 'submittal' }

/* ── Date helper ─────────────────────────────────────────────────────────── */

function fmtDate(str) {
  if (!str) return ''
  const [y, m, d] = str.split('-')
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return months[parseInt(m, 10) - 1] + ' ' + parseInt(d, 10) + ', ' + y
}

/* ── Step dots ───────────────────────────────────────────────────────────── */

function StepDots({ step }) {
  return (
    <div style={{ display: 'flex', gap: 6, padding: '10px 20px', borderBottom: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-subtle)', flexShrink: 0 }}>
      {[1, 2, 3].map(i => (
        <div key={i} className={`wf-step-dot${i < step ? ' done' : i === step ? ' active' : ''}`} />
      ))}
    </div>
  )
}

/* ── Step 1: Template selection ──────────────────────────────────────────── */

function TemplateStep({ templates, spoke, setSpoke, search, setSearch, selectedTpl, setSelectedTpl }) {
  const filtered = (templates || []).filter(t => {
    const name = (t.fields['Name'] || '').toLowerCase()
    const spokes = t.fields['Spoke'] || []
    return (spoke === 'All' || spokes.indexOf(spoke) !== -1) &&
           (!search || name.includes(search.toLowerCase()))
  })

  return (
    <>
      <div className="aj-sc-spoke-filters">
        {SPOKES.map(s => (
          <button key={s} className={`aj-sc-spoke-btn${spoke === s ? ' active' : ''}`} onClick={() => setSpoke(s)}>{s}</button>
        ))}
      </div>
      <input
        className="aj-sc-tpl-search"
        type="text"
        placeholder="Search templates..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        autoFocus
      />
      <div className="aj-sc-tpl-list">
        {templates === null ? (
          <div className="aj-sc-tpl-empty">Loading templates...</div>
        ) : filtered.length === 0 ? (
          <div className="aj-sc-tpl-empty">No templates match.</div>
        ) : filtered.map(t => {
          const f = t.fields
          const name = f['Name'] || 'Untitled'
          const beh = (f['Milestone Behavior'] || [])[0] || ''
          const phases = (f['Phase'] || []).join(', ')
          const clCt = parseInt(f['Checklist Item Count'] || 0, 10)
          const isSel = selectedTpl?.id === t.id
          const behCls = beh === 'Active' ? ' behavior-active'
            : beh === 'Reactive' ? ' behavior-reactive'
            : beh === 'Fixed'    ? ' behavior-fixed' : ''
          return (
            <div key={t.id} className={`aj-sc-tpl-card${isSel ? ' selected' : ''}`} onClick={() => setSelectedTpl(t)}>
              <div className="aj-sc-tpl-radio">
                {isSel && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <polyline points="1.5,5 4,7.5 8.5,2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <div className="aj-sc-tpl-body">
                <div className="aj-sc-tpl-name">{name}</div>
                <div className="aj-sc-tpl-tags">
                  {beh && <span className={`aj-sc-tpl-tag${behCls}`}>{beh}</span>}
                  {phases && <span className="aj-sc-tpl-tag">{phases}</span>}
                </div>
              </div>
              {clCt > 0 && <div className="aj-sc-tpl-checklist-count">{clCt} items</div>}
            </div>
          )
        })}
      </div>
    </>
  )
}

/* ── Step 2: Configure ───────────────────────────────────────────────────── */

function ConfigureStep({ phaseName, subtitle, setSubtitle, est, setEst, lft, setLft, lftInvalid, caKind, contracts, contractId, setContractId }) {
  return (
    <div className="aj-sc-config-body">
      {phaseName && (
        <div className="aj-sc-config-field">
          <label className="aj-sc-config-label">Phase</label>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 14, color: 'var(--aj-text)', padding: '4px 0' }}>{phaseName}</div>
        </div>
      )}
      {caKind && (
        <div className="aj-sc-config-field">
          <label className="aj-sc-config-label">
            Contract <span style={{ color: '#c0392b' }}>*</span>
          </label>
          <Select
            value={contractId}
            onChange={setContractId}
            options={(contracts || []).map(c => ({ value: c.id, label: c.contractor_name || 'Contract' }))}
            placeholder={contracts === null ? 'Loading contracts…' : (contracts.length ? '— select a contract —' : 'No contracts on this project')}
            style={{ width: '100%' }}
          />
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', marginTop: 4 }}>
            This also creates a draft {CA_KIND_LABEL[caKind]} record linked to the selected contract.
          </div>
        </div>
      )}
      <div className="aj-sc-config-field">
        <label className="aj-sc-config-label">
          Subtitle <span style={{ color: 'var(--aj-text-muted)', fontWeight: 400 }}>(optional)</span>
        </label>
        <input
          className="aj-sc-config-input"
          type="text"
          placeholder="e.g. PPC 1st Submission"
          value={subtitle}
          onChange={e => setSubtitle(e.target.value)}
          autoFocus
        />
      </div>
      <div className="aj-sc-config-field">
        <label className="aj-sc-config-label">
          Earliest Start <span style={{ color: 'var(--aj-text-muted)', fontWeight: 400 }}>(optional)</span>
        </label>
        <input className="aj-sc-config-input" type="date" value={est} onChange={e => setEst(e.target.value)} />
      </div>
      <div className="aj-sc-config-field">
        <label className="aj-sc-config-label">
          Due Date <span style={{ color: '#c0392b' }}>*</span>
        </label>
        <input
          className="aj-sc-config-input"
          type="date"
          value={lft}
          onChange={e => setLft(e.target.value)}
          style={lftInvalid ? { borderColor: '#c0392b' } : {}}
        />
        {lftInvalid && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b', marginTop: 4 }}>Due date is required.</div>}
      </div>
    </div>
  )
}

/* ── Step 3: Review ──────────────────────────────────────────────────────── */

function ReviewStep({ tplName, subtitle, phaseName, est, lft, clCt, contractName }) {
  return (
    <div className="aj-sc-confirm-body">
      <div className="aj-sc-confirm-card">
        <div className="aj-sc-confirm-name">
          {tplName}
          {subtitle && <span style={{ fontWeight: 400, color: 'var(--aj-text-muted)' }}> {subtitle}</span>}
        </div>
        <div className="aj-sc-confirm-row">
          <span className="aj-sc-confirm-key">Phase</span>
          <span className="aj-sc-confirm-val">{phaseName || <span style={{ color: '#c0392b' }}>Not set</span>}</span>
        </div>
        {contractName != null && (
          <div className="aj-sc-confirm-row">
            <span className="aj-sc-confirm-key">Contract</span>
            <span className="aj-sc-confirm-val">{contractName}</span>
          </div>
        )}
        {est && (
          <div className="aj-sc-confirm-row">
            <span className="aj-sc-confirm-key">Starts</span>
            <span className="aj-sc-confirm-val">{fmtDate(est)}</span>
          </div>
        )}
        <div className="aj-sc-confirm-row">
          <span className="aj-sc-confirm-key">Due date</span>
          <span className="aj-sc-confirm-val">{lft ? fmtDate(lft) : <span style={{ color: '#c0392b' }}>Not set</span>}</span>
        </div>
        <div className="aj-sc-confirm-row">
          <span className="aj-sc-confirm-key">Checklist items</span>
          <span className="aj-sc-confirm-val">{clCt} will be created</span>
        </div>
      </div>
    </div>
  )
}

/* ── Step 4: Progress ────────────────────────────────────────────────────── */

function ProgressStep({ steps }) {
  return (
    <div className="aj-sc-progress-steps">
      {steps.map((s, i) => (
        <div key={i} className="aj-sc-progress-step">
          <div className={`aj-sc-progress-icon${s.state !== 'pending' ? ' ' + s.state : ''}`}>
            {s.state === 'done' || s.state === 'running' ? (
              s.state === 'done' ? (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <polyline points="1.5,5 4,7.5 8.5,2.5" stroke="#1D9E75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : '…'
            ) : s.state === 'error' ? '!' : '·'}
          </div>
          <div className={`aj-sc-progress-text${s.state === 'pending' ? ' muted' : ''}`}>{s.text}</div>
        </div>
      ))}
    </div>
  )
}

/* ── Main component ──────────────────────────────────────────────────────── */

export default function AddMilestoneWizard() {
  const [open,        setOpen]        = useState(false)
  const [step,        setStep]        = useState(1)
  const [phId,        setPhId]        = useState(null)
  const [phaseName,   setPhaseName]   = useState('')

  // Step 1
  const [templates,    setTemplates]   = useState(null)   // null = not yet loaded
  const [selectedTpl,  setSelectedTpl] = useState(null)
  const [spoke,        setSpoke]       = useState('All')
  const [search,       setSearch]      = useState('')

  // Step 2
  const [subtitle,    setSubtitle]   = useState('')
  const [est,         setEst]        = useState('')
  const [lft,         setLft]        = useState('')
  const [lftInvalid,  setLftInvalid] = useState(false)
  // Contract picker — only relevant for the ASI/RFI Response/Submittal Review templates, each
  // 1:1 with a Supabase row (asis/rfis/submittals) scoped to a construction_contract. A milestone
  // alone doesn't know which contract it belongs to, so this is required before one of these
  // template's milestones can be created.
  const [contracts,   setContracts]  = useState(null)   // null = not yet loaded
  const [contractId,  setContractId] = useState('')

  // Step 4
  const [progress,    setProgress]   = useState([])
  const [createError, setCreateError] = useState('')

  const caKind = selectedTpl?.id === ASI_TEMPLATE_ID ? 'asi'
    : selectedTpl?.id === RFI_RESPONSE_TEMPLATE_ID ? 'rfi'
    : selectedTpl?.id === SUBMITTAL_REVIEW_TEMPLATE_ID ? 'submittal'
    : null

  /* Listen for open event */
  useEffect(() => {
    function onOpen(e) {
      const pid = e.detail?.phId || null
      const ph  = pid ? (ctx.state?.phases || []).find(p => p.id === pid) : null
      const pn  = ph?.fields['Phase Name'] || ''
      setPhId(pid)
      setPhaseName(pn)
      setStep(1)
      setSelectedTpl(null)
      setSpoke('All')
      setSearch('')
      setSubtitle('')
      setEst('')
      setLft('')
      setLftInvalid(false)
      setContracts(null)
      setContractId('')
      setProgress([])
      setCreateError('')
      setTemplates(null)   // trigger load
      setOpen(true)
    }
    window.addEventListener('open-add-ms-modal', onOpen)
    return () => window.removeEventListener('open-add-ms-modal', onOpen)
  }, [])

  /* Load templates when modal opens */
  useEffect(() => {
    if (!open || templates !== null) return
    getMilestoneTemplates()
      .then(records => setTemplates(records))
      .catch(() => setTemplates([]))
  }, [open, templates])

  /* Preload the project's contracts as soon as an ASI/RFI Response/Submittal Review template is
     selected, so the picker is ready by the time the user reaches Step 2. */
  useEffect(() => {
    if (!open || !caKind || contracts !== null) return
    getProjectContracts(ctx.recordId)
      .then(setContracts)
      .catch(() => setContracts([]))
  }, [open, caKind, contracts])

  /* Escape key */
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open && step < 4) close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, step])

  function close() { setOpen(false) }

  function handleNext() {
    if (step === 1) {
      if (!selectedTpl) return
      setStep(2)
    } else if (step === 2) {
      if (!lft) { setLftInvalid(true); return }
      if (caKind && !contractId) return
      setLftInvalid(false)
      setStep(3)
    }
  }

  async function handleCreate() {
    if (!selectedTpl || !lft || (caKind && !contractId)) return

    /* No "team assignments" step. The wizard used to stamp the deliverable type's
       Default Responsible/Accountable/Supportive onto the new milestone as RASCI rows; RASCI was
       retired 2026-09-02 (docs/FLOW_RECONCILIATION_PLAN.md decisions 2 + 3) — a queued milestone
       has NO owner by design, and gets a crew only when the tower launches it on the Flight
       Board. Those three Deliverable Type fields are now unused. */
    const steps = [
      { text: 'Creating milestone record', state: 'running' },
      { text: 'Copying checklist items',    state: 'pending' },
      ...(caKind ? [{ text: 'Linking ' + CA_KIND_LABEL[caKind] + ' record', state: 'pending' }] : []),
    ]
    setStep(4)
    setProgress([...steps])

    try {
      /* Step 1 — create milestone */
      const defDur = parseInt(selectedTpl.fields['Default Days Budgeted'] || 10, 10)
      let estStr = est || ''
      if (!estStr) {
        const lftDate = new Date(lft + 'T00:00:00')
        lftDate.setDate(lftDate.getDate() - defDur)
        estStr = lftDate.getFullYear() + '-'
          + String(lftDate.getMonth() + 1).padStart(2, '0') + '-'
          + String(lftDate.getDate()).padStart(2, '0')
      }

      const projId = ctx.recordId
      const delFields = {
        'Project':               [projId],
        'Deliverable':           [selectedTpl.id],
        'Project Phase':         phaseName,
        'Latest Finish (LFT)':   lft,
        'Earliest Start (EST)':  estStr,
        'Duration':              defDur,
        'Created by Automation': true,
        'Create Slack Thread':   true,
      }
      if (phId)     delFields['Phase']    = [phId]
      if (subtitle) delFields['Subtitle'] = subtitle

      const newId = await createMilestone(delFields)

      steps[0] = { ...steps[0], state: 'done' }
      steps[1] = { ...steps[1], state: 'running' }
      setProgress([...steps])

      /* Step 2 — checklist items */
      await seedMilestoneChecklist(newId)

      steps[1] = { ...steps[1], state: 'done' }
      setProgress([...steps])

      /* Step 3 (ASI/RFI Response/Submittal Review templates only) — create the matching Supabase
         row, linked back to this milestone. Non-fatal: the milestone already exists and is fully
         usable even if this fails, so a failure here doesn't abort the wizard — just leaves the
         milestone unlinked, the same state it'd be in without this step. The item can still be
         created manually from Reference > Construction and pointed at this milestone. */
      if (caKind) {
        steps[2] = { ...steps[2], state: 'running' }
        setProgress([...steps])
        try {
          if (caKind === 'asi') {
            const asiNumber = await getNextAsiNumber(projId, contractId).catch(() => null)
            const senderId = ctx.currentMember?.id || null
            const senderName = ctx.currentMember?.fields?.['Member Name'] || null
            await createAsi(contractId, projId, {
              milestone_airtable_id: newId, status: 'draft',
              asi_number: asiNumber, subject: subtitle || null,
              sender_member_id: senderId, sender_name: senderName,
            })
          } else if (caKind === 'rfi') {
            const rfiNumber = await getNextRfiNumber(projId, contractId).catch(() => null)
            await createRfi(contractId, projId, { rfi_number: rfiNumber, status: 'open', milestone_airtable_id: newId })
          } else if (caKind === 'submittal') {
            await createSubmittal(contractId, projId, { status: 'open', milestone_airtable_id: newId })
          }
          steps[2] = { ...steps[2], state: 'done' }
        } catch (_) {
          steps[2] = { ...steps[2], state: 'error' }
        }
        setProgress([...steps])
      }

      /* Reload project data + close */
      setTimeout(() => {
        close()
        if (projId && ctx.reloadProjectData) ctx.reloadProjectData(projId)
      }, 800)

    } catch {
      const errSteps = steps.map(s => s.state === 'running' ? { ...s, state: 'error' } : s)
      setProgress(errSteps)
      setCreateError('Creation failed — please try again.')
    }
  }

  if (!open) return null

  const clCt = selectedTpl ? parseInt(selectedTpl.fields['Checklist Item Count'] || 0, 10) : 0
  const stepLabel = step === 1 ? 'Step 1 of 3' : step === 2 ? 'Step 2 of 3' : step === 3 ? 'Step 3 of 3' : 'Creating...'
  const stepTitle = step === 1 ? 'Choose a template' : step === 2 ? 'Configure milestone' : step === 3 ? 'Confirm and create' : 'Setting up milestone'

  const btnBase = {
    border: 'none', borderRadius: 4, padding: '9px 20px',
    fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500,
    cursor: 'pointer',
  }

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div data-modal-id="AddMilestoneWizard" style={{
        background: 'var(--aj-bg-elevated)', borderRadius: 12, border: '0.5px solid var(--aj-border)',
        width: 460, maxWidth: '100%', maxHeight: 'calc(100vh - 32px)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
      }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)', flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 2 }}>
              {stepLabel}
            </div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text)' }}>
              {stepTitle}
            </div>
          </div>
          {step < 4 && (
            <button onClick={close} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', padding: 4 }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>

        {/* Step dots */}
        {step < 4 && <StepDots step={step} />}

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {step === 1 && (
            <TemplateStep
              templates={templates}
              spoke={spoke}       setSpoke={setSpoke}
              search={search}     setSearch={setSearch}
              selectedTpl={selectedTpl} setSelectedTpl={setSelectedTpl}
            />
          )}
          {step === 2 && (
            <ConfigureStep
              phaseName={phaseName}
              subtitle={subtitle} setSubtitle={setSubtitle}
              est={est}           setEst={setEst}
              lft={lft}           setLft={setLft}
              lftInvalid={lftInvalid}
              caKind={caKind} contracts={contracts} contractId={contractId} setContractId={setContractId}
            />
          )}
          {step === 3 && (
            <ReviewStep
              tplName={selectedTpl?.fields['Name'] || ''}
              subtitle={subtitle}
              phaseName={phaseName}
              est={est} lft={lft}
              clCt={clCt}
              contractName={caKind ? ((contracts || []).find(c => c.id === contractId)?.contractor_name || 'Not set') : null}
            />
          )}
          {step === 4 && <ProgressStep steps={progress} />}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 20px', borderTop: '0.5px solid var(--aj-border)', flexShrink: 0 }}>
          {createError
            ? <span style={{ fontSize: 12, color: '#c0392b', marginRight: 'auto' }}>{createError}</span>
            : <span style={{ flex: 1 }} />
          }
          {step > 1 && step < 4 && (
            <button onClick={() => setStep(s => s - 1)} style={{ ...btnBase, background: 'transparent', border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text-mid)' }}>
              Back
            </button>
          )}
          {step === 1 && (
            <button onClick={handleNext} disabled={!selectedTpl} className="aj-sc-modal-next" style={{ opacity: selectedTpl ? 1 : 0.5 }}>
              Next
            </button>
          )}
          {step === 2 && (
            <button onClick={handleNext} disabled={caKind && !contractId} className="aj-sc-modal-next" style={{ opacity: (caKind && !contractId) ? 0.5 : 1 }}>Review</button>
          )}
          {step === 3 && (
            <button
              onClick={handleCreate}
              disabled={!phaseName || !lft || (caKind && !contractId)}
              className="aj-sc-modal-next"
              style={{ opacity: (!phaseName || !lft || (caKind && !contractId)) ? 0.5 : 1 }}
            >
              Create milestone
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
